import type { TranslatedConsent } from '@monthio/react-components'
import type { History } from 'history'

import { trackEvent } from '@intercom/messenger-js-sdk'
import { useMediaQuery } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { LanguageContext, trackingService } from '@monthio/react-components'
import * as Sentry from '@sentry/react'
import configuration from '#config'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'

import type { BankDto, Psd2ConsentTranslation } from '../../Bank.types'
import type { RootState } from '../../Redux/reducers'
import type { BankSessionState } from '../../Redux/reducers/consumerDataPullSessionReducers/bankSessionReducer'
import type { Psd2ConsentState } from '../../Redux/reducers/consumerDataPullSessionReducers/psd2ConsentReducer'

import { transactionApiClient } from '../../../../api/transactionApiClient'
import { routeCodes } from '../../Bank.types'
import { getAllBankSessions } from '../../Redux/actions/consumerDataPullSessionActions/bankSessionActions'
import { createPsd2Consent } from '../../Redux/actions/consumerDataPullSessionActions/psd2ConsentActions'
import { useBanks } from '../../useBankHooks'
import { BankSelectorPageUpdatedLayout } from './BankSelectorPageUpdatedLayout'

interface BankSelectorProps {
	psd2Consent?: Psd2ConsentState
	bankSessions: BankSessionState
	getAllBankSessions: () => void
	createPsd2Consent: () => void
	history: History
}

const getConsentInCurrentLanguage = (
	language: string,
	consentTranslations: Psd2ConsentTranslation[],
) => {
	return (
		consentTranslations.find((x) => x.languageCode == language) ||
		consentTranslations[0]
	)
}

const getConsent = (
	language: string,
	psd2ConsentState?: Psd2ConsentState,
): Omit<TranslatedConsent, 'requiredText'> | null => {
	if (
		psd2ConsentState?.psD2ConsentTranslations == null ||
		psd2ConsentState.psD2ConsentTranslations.length === 0
	) {
		return null
	}
	const consentTranslation = getConsentInCurrentLanguage(
		language,
		psd2ConsentState.psD2ConsentTranslations,
	)!

	return {
		header: consentTranslation.header,
		text: consentTranslation.text,
		id: consentTranslation.header,
		isOptional: false,
	}
}

const trackBankSelection = (bank: BankDto) => {
	const now = Date.now()
	const { timeZone } = Intl.DateTimeFormat().resolvedOptions()

	trackingService.trackEvent('User Selected Bank', {
		bankName: bank.displayName,
		bankCentral: getBankCentralNameFromId(bank.serviceProviderId),
		bankId: bank.bankId,
		serviceProviderId: bank.serviceProviderId,
		time: now,
		timeZone,
	})

	trackEvent('bankSelected', {
		name: bank.displayName,
		bankCentral: getBankCentralNameFromId(bank.serviceProviderId),
		bankId: bank.bankId,
		serviceProviderId: bank.serviceProviderId,
	})
}

const getBankCentralNameFromId = (serviceProviderId: number) => {
	switch (serviceProviderId) {
		case 0:
			return 'No bank central'
		case 2:
			return 'Bank Data'
		case 3:
			return 'BEC'
		case 4:
			return 'Danske Bank'
		case 5:
			return 'Nordea'
		case 6:
			return 'SDC'
		case 7:
			return 'Test'
		case 9:
			return 'Custom Test'
		case 10:
			return 'Aiia'
		case 11:
			return 'DNB'
		case 12:
			return 'Enable Banking'
		case 13:
			return 'Swedbank'
		case 14:
			return 'Demo Profile'
		case 17:
			return 'Danske Bank'
		default:
			Sentry.captureMessage(
				`Selected bank could not be matched with the given bank central id: ${serviceProviderId}`,
			)
			return 'Unknown'
	}
}

const BankSelectorPage = ({
	psd2Consent,
	bankSessions,
	createPsd2Consent,
	getAllBankSessions,
	history,
}: BankSelectorProps) => {
	const { language } = useContext(LanguageContext)
	const { t } = useTranslation()
	const theme = useTheme()
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('xs'))
	const params = new URLSearchParams(window.location.search)
	const demoProfile = params.get('demoProfile')
	const { banks } = useBanks()
	const [
		recentlySearchedInitialCooldownComplete,
		setRecentlySearchedInitialCooldownComplete,
	] = useState(psd2Consent?.isConsentGiven ?? false)

	const [consentGiven, setConsentGiven] = useState(false)
	useEffect(() => {
		setConsentGiven(psd2Consent?.isConsentGiven ?? false)
		psd2Consent?.isConsentGiven && getAllBankSessions()
	}, [psd2Consent?.isConsentGiven, getAllBankSessions])

	const [redirectToOverview, setRedirectToOverview] = useState(false)
	useEffect(() => {
		if (redirectToOverview && bankSessions?.bankSessions?.length) {
			history.replace(routeCodes.bankSessionOverviewPage)
		}
		setRedirectToOverview(bankSessions?.bankSessions == null) // initial load
	}, [bankSessions, redirectToOverview, history])

	const onConsentGiven = useCallback(() => {
		trackingService.trackEvent('User Accepted Bank Consent', {
			time: Date.now(),
			timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
		})
		setConsentGiven(true)
		createPsd2Consent()
	}, [createPsd2Consent])

	useEffect(() => {
		if (!consentGiven) {
			return
		}
		const timeout = setTimeout(() => {
			setRecentlySearchedInitialCooldownComplete(true)
		}, 300)

		return () => {
			clearTimeout(timeout)
		}
	}, [consentGiven, setRecentlySearchedInitialCooldownComplete])

	const translatedConsent = {
		...(getConsent(language, psd2Consent) ?? {
			header: t('bankSelector-consent-header'),
			text: t('bankSelector-consent-text'),
			id: t('bankSelector-consent-header'),

			isOptional: false,
		}),
		requiredText: t('required'),
	}

	const onSelectBank = useCallback(
		(id: string | number): void => {
			const bank = banks.find((_bank) => _bank.id === Number(id))
			if (!bank) return
			trackBankSelection(bank)
			transactionApiClient.preloadBank(bank.id)
			history.replace({
				pathname: routeCodes.bankLoginPage,
				state: {
					monthioInternalBankId: bank.id,
					bankId: bank.bankId,
				},
			})
		},
		[banks, history],
	)

	const maxGridSize = isSmallScreen ? 8 : 9

	return (
		<BankSelectorPageUpdatedLayout
			bankSessions={bankSessions}
			history={history}
			demoProfile={demoProfile}
			banks={banks}
			recentlySearchedInitialCooldownComplete={
				recentlySearchedInitialCooldownComplete
			}
			maxGridSize={maxGridSize}
			onSelectBank={onSelectBank}
			consentGiven={consentGiven}
			translatedConsent={translatedConsent}
			onConsentGiven={onConsentGiven}
			isSmallScreen={isSmallScreen}
			isProduction={configuration.isProduction}
		/>
	)
}

interface ConsentTranslation extends TranslatedConsent {
	requiredText: string
}

export interface BankSelectorPageLayoutProps {
	bankSessions: BankSessionState
	history: History
	demoProfile: string | null
	banks: BankDto[]
	recentlySearchedInitialCooldownComplete: boolean
	maxGridSize: number
	onSelectBank: (id: number | string) => void
	consentGiven: boolean
	translatedConsent: ConsentTranslation
	onConsentGiven: () => void
	isSmallScreen: boolean
	isProduction: boolean
}

export default connect(
	(state: RootState) => {
		return {
			bankSessions: state.bankSessions,
			psd2Consent: state.psd2Consent,
		}
	},
	(dispatch: any) => {
		return {
			getAllBankSessions: () => dispatch(getAllBankSessions()),
			createPsd2Consent: () => dispatch(createPsd2Consent()),
		}
	},
)(BankSelectorPage)
